import * as React from 'react'
import { v4 as uuidv4 } from 'uuid'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useTask } from '@/modules/shared/context/TaskContext'
import { useLoadingStore } from '@/modules/shared/stores/loadingStore'
import { TaskType } from '@/app/constants/taskType'
import { MessageType } from '@/app/constants/messageType'
import { ReportType } from '@/app/constants/reportType'
import useTimesheetService from '@/modules/drivers/services/TimesheetService'
import {
  isTaskRateLimited,
  startTrackedTask,
} from '@/modules/shared/utils/startTrackedTask'
import { taskScopeKey } from '@/modules/shared/utils/taskScopeKey'
import { useScopeBusy } from '@/modules/shared/hooks/useScopeBusy'
import { hasOutOfDateQuarterRateNotice } from './useOutOfDateRateConfirm'
import { notifyTaxExemptNotices } from './taxExemptNotice'
import usePrintService from '@/modules/drivers/services/PrintService'
import type { TimesheetRowData } from './Group.types'
import { normalizeMonthKey } from './Group.utils'
import type { TimesheetViewMode } from '../components'
import axios from 'axios'
import { YearMonthsValue } from '@/modules/drivers/types/date'

interface UseGroupHandlersProps {
  companyId: string
  selectedMonthKey: string
  selectedGroup: { ids: number[] } | null
  sideCheckedIds: number[]
  drivers: Array<{
    id: number
    fullName?: string
    surname: string
    name: string
  }>
  period: YearMonthsValue
  setData: React.Dispatch<React.SetStateAction<TimesheetRowData[]>>
  setShowPrintModal: (show: boolean) => void
  setShowBonusFields: React.Dispatch<React.SetStateAction<boolean>>
  setShowCalculateRecordsModal: (show: boolean) => void
  setShowCalculateView: (show: boolean) => void
  setViewMode: (mode: TimesheetViewMode) => void
  fetchGroupedTimesheets: () => Promise<void>
  refreshRows: (ids: number[]) => Promise<void>
  /**
   * Pytanie o kierowcow z aktywnosciami zagranicznymi bez delegacji na liczony miesiac.
   * `false` przerywa przeliczenie. Opcjonalne - bez niego przeliczenie idzie dalej.
   */
  confirmForeignActivityWithoutTrip?: (driverNames: string) => Promise<boolean>
  /**
   * Pytanie o liczenie po nieaktualnym kursie kwartalnym. Bez tego callbacku brak
   * kursu przerywa przeliczenie - cichej zgody nie zakladamy (parytet z WPF).
   */
  confirmOutOfDateExchangeRate?: () => Promise<boolean>
}

const getAxiosErrorDefaultMsg = (err: any) => {
  if (axios.isAxiosError(err)) {
    const { data } = err.response
    const reasonObj = Array.isArray(data) ? data[0] : null
    const resonCode = reasonObj ? reasonObj.code : ''
    if (resonCode === 'TIMESHEET_CONTRACT_NOT_FOUND') {
      const driverFullName = reasonObj.message
      return `"${driverFullName}" nie ma przypisanej umowy, dlatego nie można wykonać przeliczenia.`
    }
    return `${reasonObj.code} : ${reasonObj.message}`
  }
  return err instanceof Error ? err.message : String(err)
}

export const useGroupHandlers = ({
  period,
  companyId,
  selectedMonthKey,
  selectedGroup,
  sideCheckedIds,
  drivers,
  setData,
  setShowPrintModal,
  setShowBonusFields,
  setShowCalculateRecordsModal,
  setShowCalculateView,
  setViewMode,
  fetchGroupedTimesheets,
  refreshRows,
  confirmForeignActivityWithoutTrip,
  confirmOutOfDateExchangeRate,
}: UseGroupHandlersProps) => {
  const { t } = useLocalize()
  const { addTask, showMessage } = useTask()
  const startLoading = useLoadingStore((s) => s.startLoading)
  const {
    precalculateTimesheet,
    calculateTimesheetLegacy,
  } = useTimesheetService()
  const { generatePrintout } = usePrintService()

  // Ten sam zakres, ktory serwer liczy przy zakladaniu zadania - dzieki temu
  // "ktos juz to liczy" widac przed wyslaniem zadania, takze gdy odpalil je kto inny.
  const scopeKey =
    companyId && period?.year && period?.months?.length
      ? taskScopeKey.timesheet(companyId, period.year, period.months)
      : null
  const scopeBusy = useScopeBusy(scopeKey)

  const handleCalculate = React.useCallback(
    async (excludedDriverIds?: number[]): Promise<boolean> => {
    try {

      const excluded = new Set((excludedDriverIds ?? []).map(String))
      const driverIdsToUse = sideCheckedIds
        .map((id) => String(id))
        .filter((id) => !excluded.has(id))

      if (driverIdsToUse.length === 0) {
        showMessage({
          text: t('timesheet.force-calculation-error'),
          type: MessageType.ERROR,
          translate: false,
        })
        return false
      }

      // Parse selected month key (format: "MM-YYYY")
      const normalizedMonthKey = normalizeMonthKey(selectedMonthKey)
      if (!normalizedMonthKey || typeof normalizedMonthKey !== 'string') {
        showMessage({
          text: t('timesheet.TIMESHEET_PERIOD_NOT_SELECTED'),
          type: MessageType.ERROR,
          translate: false,
        })
        return false
      }

      const [monthStr, yearStr] = normalizedMonthKey.split('-')
      const month = parseInt(monthStr, 10)
      const year = parseInt(yearStr, 10)

      if (isNaN(month) || isNaN(year)) {
        showMessage({
          text: t('timesheet.TIMESHEET_PERIOD_NOT_SELECTED'),
          type: MessageType.ERROR,
          translate: false,
        })
        return false
      }

      // Convert driver IDs from strings to numbers
      const driverNumbers = driverIdsToUse
        .map((id) => parseInt(id, 10))
        .filter((id) => !isNaN(id))

      if (driverNumbers.length === 0) {
        showMessage({
          text: t('timesheet.force-calculation-error'),
          type: MessageType.ERROR,
          translate: false,
        })
        return false
      }

      // Generate UUID for the request
      const requestId = uuidv4()

      // Step 1: Call precalculate first (following legacy pattern)
      // Pierwszy przebieg BEZ zgody na starszy kurs - przy `true` serwer w ogole nie
      // zglosi jego braku. Ostrzezenie wraca jako 400 (OnFinish daje 200 tylko dla samych
      // Success), wiec lapiemy je z wyjatku, pytamy i powtarzamy caly precalculate, zeby
      // nie pominac reszty walidacji. Parytet z WPF.
      const runPrecalculate = (accepted: boolean) =>
        precalculateTimesheet({
          companyId,
          drivers: driverNumbers,
          months: period.months, //[month],
          year: period.year, // year
          outOfDateQuarterExchangeRateAccepted: accepted,
          id: requestId,
        })

      let outOfDateAccepted = false
      let precalculateResult
      try {
        precalculateResult = await runPrecalculate(false)
      } catch (precalculateError) {
        if (
          !confirmOutOfDateExchangeRate ||
          !hasOutOfDateQuarterRateNotice(precalculateError)
        ) {
          throw precalculateError
        }

        if (!(await confirmOutOfDateExchangeRate())) return false
        outOfDateAccepted = true
        precalculateResult = await runPrecalculate(true)
      }

      // Check if precalculate returned errors
      // Legacy API returns { error: [...], type: 'ERROR' | 'WARNING' } on error
      // Can calculate if: result exists AND (no error OR error type is not ERROR)
      const canCalculate =
        precalculateResult &&
        (precalculateResult.error == null ||
          precalculateResult.type !== 'ERROR')

      if (precalculateResult?.error && precalculateResult.type === 'ERROR') {
        console.error('Precalculate error:', precalculateResult.error)
        showMessage({
          text: t('timesheet.force-calculation-error'),
          type: MessageType.ERROR,
          translate: false,
        })
        return false
      }

      // Kierowcy z aktywnosciami zagranicznymi bez delegacji - serwer zwraca ich jako
      // Success z wlasnym kodem (kazdy inny typ dalby 400). Pytamy tutaj, miedzy
      // precalculate a przeliczeniem, bo dopiero po precalculate to wiadomo.
      const foreignActivityNames = (
        Array.isArray(precalculateResult) ? precalculateResult : []
      )
        .filter(
          (notice: any) =>
            notice?.code === 'TIMESHEET_FOREIGN_ACTIVITY_WITHOUT_TRIP' &&
            notice?.message,
        )
        .map((notice: any) => String(notice.message))
        .join(', ')

      if (foreignActivityNames && confirmForeignActivityWithoutTrip) {
        const confirmed =
          await confirmForeignActivityWithoutTrip(foreignActivityNames)
        if (!confirmed) {
          return false
        }
      }

      notifyTaxExemptNotices(precalculateResult, t)

      // If precalculate has warnings but not errors, we can still proceed
      if (precalculateResult?.error && precalculateResult.type === 'WARNING') {
        console.warn('Precalculate warnings:', precalculateResult.error)
        showMessage({
          text: t('timesheet.force-calculation-warning'),
          type: MessageType.WARNING,
          translate: false,
        })
      }

      // Step 2: If precalculate succeeded, call calculate (following legacy pattern)
      if (canCalculate) {
        // Ten widok nie ma ustawien nagrody, wiec nie ma czego wyslac. Cel netto
        // przezywa przeliczenie po stronie silnika (TimesheetNettoAmountsCalculator.
        // GetNettoRateForPart) - wczesniej pusta lista kasowala go razem z nagroda.
        const bonusConfigurations: Array<any> = []

        try {
          // 409 to normalna odpowiedz: ktos z firmy liczy juz ten miesiac albo user
          // odswiezyl karte i kliknal ponownie. Przejmujemy biegnace zadanie zamiast
          // pokazywac blad, ktorego i tak nie da sie obsluzyc.
          const { adopted } = await startTrackedTask({
            companyId,
            type: TaskType.Timesheet,
            taskId: requestId,
            scopeKey: taskScopeKey.timesheet(
              companyId,
              period.year,
              period.months,
            ),
            start: () =>
              calculateTimesheetLegacy({
                companyId,
                drivers: driverNumbers,
                months: period.months,
                year: period.year,
                outOfDateQuarterExchangeRateAccepted: outOfDateAccepted,
                bonusConfigurations,
                id: requestId,
              }),
          })

          if (adopted) {
            // Zakres jest po okresie, nie po zaznaczeniu, wiec biegnace zadanie moze
            // obejmowac innych kierowcow - komunikat musi to powiedziec wprost.
            showMessage({
              text: t('timesheet.calculation-adopted-other-selection'),
              type: MessageType.INFO,
              translate: false,
            })
          }
          return true
        } catch (calculateError) {
          if (isTaskRateLimited(calculateError)) {
            showMessage({
              text: t('timesheet.too-many-tasks'),
              type: MessageType.WARNING,
              translate: false,
            })
            return false
          }
          // If calculate fails, show error and don't add task
          console.error('Error calculating timesheet:', calculateError)
          showMessage({
            text:
              calculateError instanceof Error
                ? calculateError.message
                : String(calculateError),
            type: MessageType.ERROR,
          })
          return false
        }
      }

      return false
    } catch (error) {
      console.error('Error precalculating timesheet:', error)
      showMessage({
        text: getAxiosErrorDefaultMsg(error),
        type: MessageType.ERROR,
      })
      return false
    }
  }, [
    sideCheckedIds,
    selectedMonthKey,
    period,
    companyId,
    precalculateTimesheet,
    calculateTimesheetLegacy,
    showMessage,
    t,
    confirmOutOfDateExchangeRate,
  ])

  const handlePrint = React.useCallback(() => {
    setShowPrintModal(true)
  }, [setShowPrintModal])

  const handleGeneratePrintout = React.useCallback(
    async (
      selectedReports: ReportType[],
      options: { generateToSeparateFiles: boolean; rememberSelected: boolean },
    ) => {
      try {
        // Get selected drivers from table context, or fall back to the
        // side-panel selection (the `drivers` list is not fetched here).
        const selectedDriverIds =
          selectedGroup?.ids && selectedGroup.ids.length > 0
            ? selectedGroup.ids.map((id) => String(id))
            : sideCheckedIds.map((id) => String(id))

        // Parse selected month key (format: "MM-YYYY")
        const normalizedMonthKey = normalizeMonthKey(selectedMonthKey)
        if (!normalizedMonthKey || typeof normalizedMonthKey !== 'string') {
          showMessage({
            text: t('timesheet.TIMESHEET_PERIOD_NOT_SELECTED'),
            type: MessageType.ERROR,
            translate: false,
          })
          return
        }

        const [monthStr, yearStr] = normalizedMonthKey.split('-')
        const month = parseInt(monthStr, 10)
        const year = parseInt(yearStr, 10)

        if (isNaN(month) || isNaN(year)) {
          showMessage({
            text: t('timesheet.TIMESHEET_PERIOD_NOT_SELECTED'),
            type: MessageType.ERROR,
            translate: false,
          })
          return
        }

        // Generate printout for each selected report type
        // For now, we'll generate them sequentially
        // In the future, if generateToSeparateFiles is false, we might combine them
        for (const reportType of selectedReports) {
          try {
            const result = await generatePrintout({
              companyId,
              reportTypes: [reportType],
              driverIds: selectedDriverIds,
              month,
              year,
              generateToSeparateFiles: options.generateToSeparateFiles,
              rememberSelected: options.rememberSelected,
            })

            // Add task to track background print generation
            if (result.taskId) {
              addTask(result.taskId, TaskType.Printout, null)
            }
          } catch (error) {
            console.error(`Error generating printout for ${reportType}:`, error)
            showMessage({
              text: error instanceof Error ? error.message : String(error),
              type: MessageType.ERROR,
            })
          }
        }

     

        // Close modal
        setShowPrintModal(false)
      } catch (error) {
        console.error('Error generating printout:', error)
        showMessage({
          text: error instanceof Error ? error.message : String(error),
          type: MessageType.ERROR,
        })
      }
    },
    [
      selectedGroup?.ids,
      selectedMonthKey,
      sideCheckedIds,
      companyId,
      generatePrintout,
      addTask,
      t,
      setShowPrintModal,
    ],
  )

  const handleClear = React.useCallback(() => {
    console.log('Clear calculation')
    // TODO: Clear calculation state
  }, [])

  const handleDriverClick = React.useCallback(
    async (driverId: string, date: string) => {
      if (!companyId || !driverId || !date) {
        return
      }

      // Refresh through the same lean groupedTimesheet fetch + mapping the grid
      // uses. The previous version called the heavy driverTimesheet endpoint (full
      // model with all events) and then mapped response fields that endpoint does
      // not even return, so every field fell back to the existing row value.
      await refreshRows([parseInt(driverId, 10)])
    },
    [companyId, refreshRows],
  )

  const handleRefresh = React.useCallback(async () => {
    const stopLoading = startLoading()
    try {
      await fetchGroupedTimesheets()
    } catch (error) {
      console.error('Error refreshing timesheet data:', error)
    } finally {
      stopLoading()
    }
  }, [startLoading, fetchGroupedTimesheets])

  const handleBonusReward = React.useCallback(() => {
    setShowBonusFields((prev) => !prev)
  }, [setShowBonusFields])

  const handleShowCalculateRecords = React.useCallback(() => {
    setShowCalculateRecordsModal(true)
  }, [setShowCalculateRecordsModal])

  const handleCalculateSuccess = React.useCallback(() => {
    // Refresh timesheet data after successful calculation
    // The table will automatically update when drivers are clicked
    setShowCalculateRecordsModal(false)
    setShowCalculateView(false)
  }, [setShowCalculateRecordsModal, setShowCalculateView])

  const handleImportFile = React.useCallback(() => {
    setShowCalculateRecordsModal(true)
  }, [setShowCalculateRecordsModal])

  const handleCalculateFromData = React.useCallback(() => {
    setShowCalculateRecordsModal(true)
  }, [setShowCalculateRecordsModal])

  const handleToggleOvertimeCompensation = React.useCallback(
    (enabled: boolean) => {
      console.log('Overtime compensation:', enabled)
      // TODO: Update overtime compensation setting
    },
    [],
  )

  const handleViewToggle = React.useCallback(
    (view: TimesheetViewMode) => {
      setViewMode(view)
    },
    [setViewMode],
  )

  const handleSave = React.useCallback(() => {
    console.log('Save clicked')
    // TODO: Implement save functionality
  }, [])

  const handleAmountView = React.useCallback(() => {
    console.log('Amount view clicked')
    // TODO: Implement amount view functionality
  }, [])

  const handleForeignAmounts = React.useCallback(() => {
    console.log('Foreign amounts clicked')
    // TODO: Implement foreign amounts functionality
  }, [])

  return {
    handleCalculate,
    /** Pozwala zablokowac przycisk i powiedziec, kto trzyma okres. */
    scopeBusy,
    handlePrint,
    handleGeneratePrintout,
    handleClear,
    handleDriverClick,
    handleRefresh,
    handleBonusReward,
    handleShowCalculateRecords,
    handleCalculateSuccess,
    handleImportFile,
    handleCalculateFromData,
    handleToggleOvertimeCompensation,
    handleViewToggle,
    handleSave,
    handleAmountView,
    handleForeignAmounts,
  }
}
